/**
 * Calculates the distance between two points in meters using the Haversine formula.
 * @param {Object} point1 - { lat: number, lng: number }
 * @param {Object} point2 - { lat: number, lng: number }
 * @returns {number} Distance in meters
 */
export const getDistance = (point1, point2) => {
    if (!point1 || !point2) return 0;

    const R = 6371e3; // Earth radius in meters
    const lat1 = point1.lat * Math.PI / 180;
    const lat2 = point2.lat * Math.PI / 180;
    const deltaLat = (point2.lat - point1.lat) * Math.PI / 180;
    const deltaLng = (point2.lng - point1.lng) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

/**
 * Checks if a point is inside a circle.
 * @param {Object} point - { lat: number, lng: number }
 * @param {Object} center - { lat: number, lng: number }
 * @param {number} radius - Radius in meters
 * @returns {boolean}
 */
export const isPointInCircle = (point, center, radius) => {
    if (!point || !center || radius == null) return false;
    return getDistance(point, center) <= radius;
};

/**
 * Checks if a point is inside a polygon using the Ray-Casting algorithm.
 * @param {Object} point - { lat: number, lng: number }
 * @param {Array} polygon - Array of { lat: number, lng: number }
 * @returns {boolean}
 */
export const isPointInPolygon = (point, polygon) => {
    if (!point || !polygon || polygon.length < 3) return false;

    const x = point.lat;
    const y = point.lng;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lat, yi = polygon[i].lng;
        const xj = polygon[j].lat, yj = polygon[j].lng;

        const intersect = ((yi > y) !== (yj > y)) &&
            (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

        if (intersect) inside = !inside;
    }

    return inside;
};
